/* ═══════════════════════════════════════════════════════
   sheet — the pieces both public sheets are built from.
   ═══════════════════════════════════════════════════════
   Letterhead, section heading, tiles, fact list, checkbox list, footer, and
   the small formatters (time, date, phone links, newline text -> lines). The
   Call Sheet (CrewCardView) and the Session Sheet (ClientCardView) compose
   these; the stylesheet is sheetStyles.js. This folder imports nothing but
   React and its own files, so the /card bundle stays small.

   Constants the sheet needs are literals here on purpose: the card entry may
   not import src/lib (see the note in main-card.jsx). */
import React, { useState } from "react";
import { SHEET_CSS } from "./sheetStyles.js";
import logo from "./nyc-lockup.png";

export const STUDIO_ADDRESS = "62 Flushing Ave, Brooklyn, NY 11205";
export const STUDIO_MAP = "https://maps.app.goo.gl/vvesLB7iVuinAt8p6";

/* "15:00" -> "3:00 PM"; "15" -> "3:00 PM"; "" -> "". Only a strict time is
   converted: "H:mm" / "HH:mm" or a bare hour, hours 0..23, minutes 0..59.
   Anything else ("3 PM", "15:30 PM", "24:00") is returned as typed. */
const STRICT_TIME_RE = /^(\d{1,2})(?::(\d{2}))?$/;
export const fmtTime = (t) => {
  if (!t) return "";
  const m = STRICT_TIME_RE.exec(String(t));
  if (!m) return String(t);
  const h = Number(m[1]);
  const mm = m[2] ?? "00";
  if (h > 23 || Number(mm) > 59) return String(t);
  return `${h === 0 ? 12 : h > 12 ? h - 12 : h}:${mm} ${h >= 12 ? "PM" : "AM"}`;
};
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const localDate = (iso) => { const [y, m, d] = iso.split("-").map(Number); return new Date(y, m - 1, d); };
/* "2026-09-22" -> "Tuesday, September 22" (the big date on both sheets). */
export const fmtDate = (iso) => DATE_RE.test(iso || "")
  ? localDate(iso).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })
  : (iso || "");
/* "2026-09-21" -> "Mon, Sep 21" (the footer's sent date). */
export const fmtDateShort = (iso) => DATE_RE.test(iso || "")
  ? localDate(iso).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })
  : (iso || "");
const digits = (p) => String(p || "").replace(/\D/g, "").replace(/^1/, "");
export const telHref = (p) => "tel:+1" + digits(p);
export const smsHref = (p) => "sms:+1" + digits(p);

/* A newline text field as the sheet prints it: one entry per non-empty line,
   a leading "-", "•" or "*" (a bullet someone typed) dropped. Stored text is
   never rewritten; this only decides what each <li> says. */
export const splitLines = (text) => String(text || "")
  .split(/\r?\n/)
  .map((l) => l.replace(/^\s*[-•*]\s*/, "").trim())
  .filter(Boolean);

/* Snapshot fields are normalised by PRESENCE, never by `v`: a stored v1
   sheet has no `gear` array, no `onSite`, no `style`, and it must keep
   rendering. */
export const asList = (x) => (Array.isArray(x) ? x : x ? [x] : []);
export const isCool = (snap) => snap?.style === "cool";

/* Root: the inline-size container (phone rules key off its width, so the
   admin Preview at 600px shows the page layout) around the paper. */
export function Sheet({ snap, kind, children }) {
  return (
    <div className="sheet-wrap">
      <style>{SHEET_CSS}</style>
      <article className="sheet" data-kind={kind} data-style={isCool(snap) ? "cool" : undefined}>
        {children}
      </article>
    </div>
  );
}

/* Lockup left; mono meta block right (the document name, then one line of
   ids); the 2px rule under both. */
export function Letterhead({ doc, line }) {
  return (<>
    <div className="lh">
      <img src={logo} alt="New York Capture" />
      <div className="meta"><div className="doc">{doc}</div>{line ? <div>{line}</div> : null}</div>
    </div>
    <hr className="r2" />
  </>);
}

export function Section({ title, className = "", children }) {
  return <div className={"sec" + (className ? ` ${className}` : "")}><h3>{title}</h3>{children}</div>;
}

/* Two columns on the page, one on a phone. With a single child (the other
   section empty and gone) it renders that child full width instead of a
   half-empty grid. */
export function Cols({ children }) {
  const kids = React.Children.toArray(children);
  if (!kids.length) return null;
  if (kids.length === 1) return kids[0];
  return <div className="g2">{kids}</div>;
}

/* The big numbers under the date. `hot` marks the one the reader acts on
   (Cool paints it orange). A non-time value (e.g. "To be confirmed") takes
   `text` so it wraps instead of overflowing the tile. */
export function Tiles({ tiles }) {
  const shown = tiles.filter((t) => t && t.value);
  if (!shown.length) return null;
  const cls = shown.length === 1 ? " one" : shown.length === 2 ? " two" : "";
  return (
    <div className={"tiles" + cls}>
      {shown.map((t) => (
        <div className={"tile" + (t.hot ? " hot" : "")} key={t.label}>
          <div className="k">{t.label}</div>
          <div className={"v" + (t.text ? " txt" : "")}>{t.value}</div>
        </div>
      ))}
    </div>
  );
}

/* Facts, one per line. Takes the raw newline string or an array of lines. */
export function FactList({ text, lines }) {
  const items = lines ? lines.map((l) => String(l || "").trim()).filter(Boolean) : splitLines(text);
  if (!items.length) return null;
  return <ul className="f">{items.map((l, i) => <li key={i}>{l}</li>)}</ul>;
}

/* Boxes to tick. `ticks` makes them tickable on this device (never stored;
   a printed sheet is ticked with a pen). */
export function CheckList({ items, ticks = false }) {
  const [done, setDone] = useState({});
  const list = (items || []).map((t) => String(t || "").trim()).filter(Boolean);
  if (!list.length) return null;
  const flip = (i) => setDone((d) => ({ ...d, [i]: !d[i] }));
  return (
    <ul className="c">
      {list.map((t, i) => (
        <li key={i} className={done[i] ? "done" : undefined}>
          {ticks
            ? <span className={"bx" + (done[i] ? " on" : "")} role="checkbox" aria-checked={!!done[i]} aria-label={t} tabIndex={0}
                onClick={() => flip(i)}
                onKeyDown={(e) => { if (e.key === " " || e.key === "Enter") { e.preventDefault(); flip(i); } }} />
            : <span className="bx" />}
          <span className="t">{t}</span>
        </li>
      ))}
    </ul>
  );
}

export function Foot({ left, right }) {
  return <div className="foot"><span>{left}</span>{right ? <span>{right}</span> : null}</div>;
}
